import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib_app/api";
import { toast } from "sonner";
import Avatar, { fileToAvatarDataURL } from "@/components/app/Avatar";
import DateInput from "@/components/app/DateInput";
import { User, Briefcase, Phone, Save, Shield, Upload, KeyRound, Trash2, UserCheck, UserX, MapPin, X } from "lucide-react";
import IconAction from "@/components/app/IconAction";

const GENDER = [
  { v: "male", label: "Laki-laki" },
  { v: "female", label: "Perempuan" },
  { v: "other", label: "Lainnya" },
];
const EMPLOYMENT = [
  { v: "full_time", label: "Full-time" },
  { v: "contract", label: "Kontrak" },
  { v: "intern", label: "Intern" },
];

const ROLE_META = {
  owner: { label: "Owner", chip: "bg-[#FFDB52] text-[#7A5B00] border-[#F5D66B]" },
  sales_director: { label: "Sales Director", chip: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]" },
  sales: { label: "Sales", chip: "bg-[#C9F0D6] text-[#4A6D53] border-[#8FD4A2]" },
  finance: { label: "Finance", chip: "bg-[#FFE89A] text-[#7A6633] border-[#FFDB52]" },
};

const randPw = () => {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

export default function TeamMemberProfileModal({ user, users, currentUserId, onOpenChange, onSaved }) {
  const [tab, setTab] = useState("personal");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const avatarRef = useRef(null);
  const isSelf = user && currentUserId && user.user_id === currentUserId;

  useEffect(() => {
    if (!user) return;
    setTab("personal");
    setConfirmDelete(false);
    setResetPw(randPw());
    (async () => {
      try {
        const { data } = await api.get(`/users/${user.user_id}/profile`);
        setForm(data);
      } catch {
        setForm(user);
      }
    })();
  }, [user]);

  if (!user) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const savePersonalContact = async () => {
    setBusy(true);
    try {
      const payload = {
        name: (form.name || "").trim() || user.name,
        nickname: form.nickname || "",
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || "",
        city: form.city || "",
        language: form.language || "",
        phone: form.phone || "",
        linkedin: form.linkedin || "",
        alt_email: form.alt_email || "",
      };
      await api.patch(`/users/${user.user_id}/profile`, payload);
      toast.success("Profil diperbarui");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const saveWork = async () => {
    setBusy(true);
    try {
      const payload = {
        job_title: form.job_title || "",
        team: form.team || "",
        manager_id: form.manager_id || null,
        employment_type: form.employment_type || "",
        work_area: form.work_area || "",
        join_date: form.join_date || null,
      };
      await api.patch(`/users/${user.user_id}/work`, payload);
      toast.success("Info pekerjaan diperbarui");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  // ----- Akun tab actions -----
  const saveAccount = async () => {
    setBusy(true);
    try {
      const emailChanged = (form.email || "") !== (user.email || "");
      if (emailChanged) {
        await api.patch(`/users/${user.user_id}`, { name: user.name, email: form.email });
      }
      if ((form.role || user.role) !== user.role && !isSelf) {
        await api.patch(`/users/${user.user_id}/role`, { role: form.role });
      }
      toast.success("Akun diperbarui");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const doToggleActive = async () => {
    if (isSelf) return toast.error("Tidak bisa menonaktifkan diri sendiri.");
    const newState = !user.is_active;
    if (!newState && !window.confirm(`Nonaktifkan ${user.name}? Data pencapaiannya tetap tersimpan.`)) return;
    setBusy(true);
    try {
      await api.patch(`/users/${user.user_id}/active`, { is_active: newState });
      toast.success(newState ? "User diaktifkan" : "User dinonaktifkan");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const doResetPassword = async () => {
    if (!resetPw || resetPw.length < 6) return toast.error("Password minimal 6 karakter.");
    setBusy(true);
    try {
      await api.post(`/users/${user.user_id}/reset_password`, { password: resetPw });
      toast.success(`Password baru: ${resetPw}. Bagikan ke user.`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal reset");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (isSelf) return toast.error("Tidak bisa menghapus diri sendiri.");
    setBusy(true);
    try {
      await api.delete(`/users/${user.user_id}`);
      toast.success("User dihapus");
      onSaved?.();
      onOpenChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus");
    } finally { setBusy(false); }
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Harus gambar");
    setBusy(true);
    try {
      const dataUrl = await fileToAvatarDataURL(f);
      const r = await api.post(`/users/${user.user_id}/avatar`, { picture: dataUrl });
      setForm((s) => ({ ...s, picture: r.data?.picture || dataUrl }));
      toast.success("Foto diperbarui");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onOpenChange()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto rounded-3xl border-0 p-0"
        data-testid="team-profile-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Edit Profil Tim {user.name}</DialogTitle>
        <DialogDescription className="sr-only">Ubah info pribadi, kontak, pekerjaan, dan akun anggota tim.</DialogDescription>

        <div className="p-6 space-y-5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold">Edit Profil Tim</p>

          {/* HERO */}
          <div
            className="rounded-2xl p-5 backdrop-blur-xl"
            style={{
              background: "linear-gradient(135deg, rgba(190,210,240,0.55), rgba(232,240,254,0.35))",
              border: "1px solid rgba(150,175,220,0.45)",
              boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
            }}
          >
            <div className="flex items-start gap-4">
              <div className="relative shrink-0">
                <Avatar name={form.name || user.name} picture={form.picture || user.picture} size="lg" className="!w-20 !h-20 !text-2xl ring-4 ring-white shadow-lg" />
                <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
                <button
                  type="button"
                  onClick={() => avatarRef.current?.click()}
                  disabled={busy}
                  data-testid="team-upload-avatar-btn"
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#090808] hover:bg-black text-white flex items-center justify-center shadow-lg border-2 border-white transition"
                  title="Ganti foto"
                >
                  <Upload className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  {(() => {
                    const rm = ROLE_META[form.role || user.role] || ROLE_META.sales;
                    return <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${rm.chip}`}>{rm.label}</span>;
                  })()}
                  {form.employment_type && (
                    <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80">
                      {(EMPLOYMENT.find((e) => e.v === form.employment_type) || {}).label}
                    </span>
                  )}
                  {user.is_active === false && (
                    <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#F4F5F7] text-[#A0A3A8] border border-[#DCE0E7]">Nonaktif</span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-[#090808] leading-tight truncate">{form.name || user.name}</h2>
                <div className="mt-1 flex items-center flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#626061]">
                  {form.nickname && <span>@{form.nickname}</span>}
                  {form.email && <span>{form.email}</span>}
                  {form.job_title && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {form.job_title}</span>}
                  {form.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {form.city}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* TAB BAR */}
          <div className="flex items-center border-b border-[#DCE0E7] overflow-x-auto">
            <TabBtn active={tab === "personal"} onClick={() => setTab("personal")} icon={User} label="Pribadi" />
            <TabBtn active={tab === "contact"} onClick={() => setTab("contact")} icon={Phone} label="Kontak" />
            <TabBtn active={tab === "work"} onClick={() => setTab("work")} icon={Briefcase} label="Pekerjaan" />
            <TabBtn active={tab === "account"} onClick={() => setTab("account")} icon={Shield} label="Akun" testid="team-tab-account" />
          </div>

          {/* TAB CONTENT — Client-style container with auto input styling */}
          <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5 [&_input:not([type=checkbox]):not([type=radio])]:h-11 [&_input:not([type=checkbox]):not([type=radio])]:rounded-xl [&_input:not([type=checkbox]):not([type=radio])]:bg-white [&_[role=combobox]]:h-11 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:bg-white">
            {tab === "personal" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nama Lengkap"><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
                <Field label="Nama Panggilan"><Input value={form.nickname || ""} onChange={(e) => set("nickname", e.target.value)} placeholder="Sapaan singkat" /></Field>
                <Field label="Tanggal Lahir"><DateInput value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
                <Field label="Gender">
                  <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{GENDER.map((g) => <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Kota"><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} placeholder="Jakarta" /></Field>
                <Field label="Bahasa"><Input value={form.language || ""} onChange={(e) => set("language", e.target.value)} placeholder="Indonesia, English" /></Field>
              </div>
            )}
            {tab === "contact" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nomor HP / WhatsApp"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="0812xxx" /></Field>
                <Field label="Email Alternatif"><Input type="email" value={form.alt_email || ""} onChange={(e) => set("alt_email", e.target.value)} placeholder="nama@personal.com" /></Field>
                <div className="md:col-span-2">
                  <Field label="LinkedIn URL"><Input value={form.linkedin || ""} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://linkedin.com/in/..." /></Field>
                </div>
              </div>
            )}
            {tab === "work" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Jabatan"><Input value={form.job_title || ""} onChange={(e) => set("job_title", e.target.value)} placeholder="Account Executive" /></Field>
                <Field label="Team / Divisi"><Input value={form.team || ""} onChange={(e) => set("team", e.target.value)} placeholder="Sales · Enterprise" /></Field>
                <Field label="Atasan">
                  <Select value={form.manager_id || "none"} onValueChange={(v) => set("manager_id", v === "none" ? null : v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Tidak ada —</SelectItem>
                      {(users || []).filter((u) => u.user_id !== user.user_id).map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Tipe">
                  <Select value={form.employment_type || ""} onValueChange={(v) => set("employment_type", v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{EMPLOYMENT.map((e) => <SelectItem key={e.v} value={e.v}>{e.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Wilayah Kerja"><Input value={form.work_area || ""} onChange={(e) => set("work_area", e.target.value)} placeholder="Jakarta / Nasional" /></Field>
                <Field label="Tanggal Bergabung"><DateInput value={form.join_date || ""} onChange={(e) => set("join_date", e.target.value)} /></Field>
              </div>
            )}
            {tab === "account" && (
              <div className="space-y-4" data-testid="team-account-tab">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Email">
                    <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="team-account-email" />
                  </Field>
                  <Field label="Role">
                    <Select value={form.role || user.role} onValueChange={(v) => set("role", v)} disabled={isSelf}>
                      <SelectTrigger data-testid="team-account-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="sales_director">Sales Director</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                {user.provider === "local" && (
                  <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <KeyRound className="w-4 h-4 text-[#B39548]" />
                      <div className="text-sm font-bold text-[#090808]">Reset Password</div>
                    </div>
                    <p className="text-[11px] text-[#A0A3A8] mb-3">Buat password baru untuk user ini. Password lama akan tidak berlaku.</p>
                    <div className="flex gap-2">
                      <Input value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="team-reset-pw-input" placeholder="Password baru" />
                      <Button type="button" variant="outline" onClick={() => setResetPw(randPw())} className="shrink-0 h-11 rounded-xl">Acak</Button>
                      <Button type="button" onClick={doResetPassword} disabled={busy} data-testid="team-reset-pw-btn" className="bg-[#FFDB52] hover:bg-amber-600 shrink-0 h-11 rounded-xl">
                        Reset
                      </Button>
                    </div>
                  </div>
                )}

                <div className="p-4 rounded-2xl border border-[#DCE0E7] flex items-center gap-3 bg-white/70">
                  {user.is_active === false ? (
                    <UserX className="w-5 h-5 text-slate-400 shrink-0" />
                  ) : (
                    <UserCheck className="w-5 h-5 text-[#57D28A] shrink-0" />
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-bold text-[#090808]">Status: {user.is_active === false ? "Nonaktif" : "Aktif"}</div>
                    <div className="text-[11px] text-[#A0A3A8] mt-0.5">
                      {user.is_active === false ? "User tidak bisa login, tetap muncul di ranking historis." : "User aktif dapat login & bekerja normal."}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={doToggleActive}
                    disabled={busy || isSelf}
                    data-testid="team-toggle-active-btn"
                    className="shrink-0 gap-1.5 h-10 rounded-full"
                  >
                    {user.is_active === false ? <><UserCheck className="w-3.5 h-3.5" /> Aktifkan</> : <><UserX className="w-3.5 h-3.5" /> Nonaktifkan</>}
                  </Button>
                </div>

                {!isSelf && (
                  <div className="p-4 rounded-2xl border-2 border-rose-200 bg-[#F8ECE5]/40">
                    <div className="flex items-center gap-2 mb-1">
                      <Trash2 className="w-4 h-4 text-[#FF6B7D]" />
                      <div className="text-sm font-bold text-[#FF6B7D]">Hapus User</div>
                    </div>
                    <p className="text-[11px] text-rose-800/70 mb-3">
                      Semua prospek yang dimiliki user akan tetap ada, tapi ownernya jadi kosong. Aksi ini permanen.
                    </p>
                    {!confirmDelete ? (
                      <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)} data-testid="team-delete-btn" className="gap-1.5 text-[#FF6B7D] border-rose-300 hover:bg-[#FFD7B5] h-10 rounded-full">
                        <Trash2 className="w-3.5 h-3.5" /> Hapus permanen…
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)} className="h-10 rounded-full">Batal</Button>
                        <Button type="button" onClick={doDelete} disabled={busy} data-testid="team-delete-confirm-btn" className="bg-[#FF6B7D] hover:bg-rose-600 gap-1.5 h-10 rounded-full">
                          <Trash2 className="w-3.5 h-3.5" /> Yakin, hapus {user.name}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="pt-2 border-t border-[#E5EEFA]/60 flex items-center justify-end gap-2">
            <IconAction icon={X} label="Tutup" onClick={() => onOpenChange()} testid="team-profile-close-btn" />
            <IconAction
              icon={Save}
              label={busy ? "Menyimpan..." : "Simpan"}
              variant="primary"
              onClick={tab === "work" ? saveWork : tab === "account" ? saveAccount : savePersonalContact}
              disabled={busy}
              testid="team-profile-save-btn"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ${
        active ? "text-[#090808] border-[#090808] font-bold" : "text-[#A0A3A8] border-transparent hover:text-[#626061]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
